import React, { useState, useEffect, useContext } from "react";
import { Table, Button, message, Space, Layout, Input, Modal, Upload, Alert } from "antd";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../Constants";
import { Content } from "antd/es/layout/layout";
import {
  ArrowLeftOutlined,
  CloseCircleOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import AppContext from "../../AppContext";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import EmployeeExcelUploadModel from "./EmployeeExcelUploadModel";

const EmployeeList = () => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const { profile, maxEmployees, employeeUsage } = useContext(AppContext);
  const navigate = useNavigate();

  // Organization-wide limit (Parent + all direct Sub-Companies). Convenience check only;
  // the backend rejects over-limit creation regardless of this flag.
  const atEmployeeLimit = maxEmployees > 0 && employeeUsage >= maxEmployees;

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    const lowercasedFilter = searchText.toLowerCase();
    const filtered = employees.filter((item) => {
      return (
        item.firstName.toLowerCase().includes(lowercasedFilter) ||
        item.lastName.toLowerCase().includes(lowercasedFilter) ||
        item.email.toLowerCase().includes(lowercasedFilter) ||
        item.companyName.toLowerCase().includes(lowercasedFilter)
      );
    });
    setFilteredData(filtered);
  }, [searchText, employees]);

  const fetchEmployees = async (take = 10, skip = 0) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/Employee?companyId=${profile.companyId}&take=${take}&skip=${skip}`
      );
      setEmployees(response.data.items);
      setFilteredData(response.data.items);
      setTotalRecords(response.data.totalRecords);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching employees:", error);
      message.error(t("messages.error.fetchCompany"));
      setLoading(false);
    }
  };

  const OnPageChange = (e) => {
    const take = e.pageSize;
    const skip = (e.current - 1) * e.pageSize;
    fetchEmployees(take, skip);
  };

  
  const columns = [
    {
      title: t("form.firstName"),
      dataIndex: "firstName",
      key: "firstName",
    },
    {
      title: t("form.lastName"),
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: t("form.email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("form.role"),
      dataIndex: "role",
      key: "role",
    },
    {
      title: t("form.profileStatus"),
      dataIndex: "profileComplete",
      key: "profileComplete",
      render: (profileComplete) => (
        <div className="flex items-center">
          {profileComplete ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t("status.complete")}
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {t("status.incomplete")}
            </span>
          )}
        </div>
      ),
    },
    // {
    //   title: t("form.company"),
    //   dataIndex: "companyName",
    //   key: "companyName",
    // },
    // {
    //   title: t("form.hireDate"),
    //   dataIndex: "hireDate",
    //   key: "hireDate",
    //   render: (text) =>
    //     new Date(text).toLocaleDateString("en-GB", {
    //       day: "2-digit",
    //       month: "short",
    //       year: "numeric",
    //     }),
    //   sorter: (a, b) => new Date(a.hireDate) - new Date(b.hireDate),
    // },
    {
      title: t("table.admin.actions"),
      key: "actions",
      render: (_, record) => (
        <div className="space-x-2">
          <button
            type="button"
            onClick={() => navigate(`/employee-form/${record.id}`)}
            className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-2 rounded flex items-center"
          >
            <EditOutlined />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Layout className="min-h-full bg-transparent">
      <header className="py-4 bg-transparent">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-white">
            {t("navigation.employee")}
          </h1>
        </div>
      </header>
      <Content className="rounded-lg px-5 py-6 shadow bg-white sm:px-6">
        <div className="bg-white">
          {atEmployeeLimit && (
            <Alert
              type="warning"
              showIcon
              className="mb-4"
              message={t(
                "messages.error.maxEmployeesReached",
                "Employee limit reached"
              )}
              description={`Your organization has reached its employee limit (${employeeUsage}/${maxEmployees}). Contact your administrator to increase the limit before adding more employees.`}
            />
          )}
          <div className="flex justify-between items-center mb-6">
            <div className="relative">
              <SearchOutlined className="absolute top-2 left-2 text-gray-400" />
              <input
                type="text"
                placeholder={t("placeholders.enterFirstName")}
                value={searchText}
                style={{ width: 300, paddingLeft: "30px" }}
                onChange={(e) => setSearchText(e.target.value)}
                className="border border-gray-300 p-2 rounded"
              />
              {searchText && (
                <CloseCircleOutlined
                  className="absolute top-2 right-2 text-gray-400 cursor-pointer"
                  onClick={() => setSearchText("")}
                />
              )}
            </div>
            <div className="space-x-2 flex gap-2 items-center">
              <button
                type="button"
                onClick={() =>
                  atEmployeeLimit
                    ? toast.error(t("messages.error.maxEmployeesReached"))
                    : setUploadModalVisible(true)
                }
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-1.5 rounded-lg flex items-center"
              >
                <UploadOutlined className="mr-2" />
                {t("buttons.uploadExcel")}
              </button>
              <button
                type="button"
                onClick={() =>
                  atEmployeeLimit
                    ? toast.error(t("messages.error.maxEmployeesReached"))
                    : navigate("/employee-form")
                }
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-1.5 rounded-lg flex items-center"
              >
                <PlusOutlined className="mr-2" />
                {t("buttons.new")}
                <span className="lg:inline-block hidden ml-1">
                  {t("navigation.employee")}
                </span>
              </button>
            </div>
          </div>
          <Table
            columns={columns}
            dataSource={filteredData}
            scroll={{ x: true }}
            rowKey="id"
            loading={loading}
            onChange={OnPageChange}
            className="[&_.ant-table-thead_.ant-table-cell]:bg-gray-100 [&_.ant-table-thead_.ant-table-cell]:text-black"
            rowClassName={(record, index) =>
              `${
                index % 2 === 0 ? "bg-white" : "bg-gray-50"
              } hover:bg-gray-100 transition-colors`
            }
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              total: totalRecords,
            }}
          />
        </div>
      </Content>

      {/* Excel Upload Modal */}
      <Modal
        title={t("modal.uploadEmployeeExcel")}
        open={uploadModalVisible}
        onOk={() => {}} // This will be handled by the child component
        onCancel={() => {
          setUploadModalVisible(false);
        }}
        footer={null} // Remove default footer since upload component handles its own actions
        width={800}
      >
        <EmployeeExcelUploadModel 
          profile={profile}
          setUploadModalVisible={setUploadModalVisible}
          fetchEmployees={fetchEmployees}
          uploadLoading={uploadLoading}
          setUploadLoading={setUploadLoading}
        />
      </Modal>
    </Layout>
  );
};

export default EmployeeList;
